import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button, IconButton, type ButtonVariant } from '../primitives/primitives.js';
import { Icon, type IconName } from '../icons/icons.js';
import styles from './overlays.module.css';

/**
 * TESSMA ONE D-8 overlays — STATUS: APPROVED AND FROZEN (v1.1).
 *
 * The frozen AI drawer is a labelled surface with a close control and a
 * backdrop that closes it. Everything here inherits that language and adds the
 * dialog behaviour the Phase 1 audit found missing (R-08): `role="dialog"`,
 * `aria-modal`, a focus trap, Escape to close, and focus restored to the
 * element that opened it.
 */

/** Traps Tab within a container and restores focus on unmount. */
function useDialogBehaviour(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  /**
   * A DEFECT REPAIR, NOT A REDESIGN — and an invisible one, which is why it
   * survived review.
   *
   * This effect opens the dialog: it moves focus to the first control and
   * restores it on the way out. It must therefore run WHEN THE DIALOG OPENS
   * and at no other time. It used to depend on `onClose` as well, and almost
   * every caller writes that inline — `onClose={() => setOpen(false)}` — which
   * is a new function on every render. So every re-render of the PARENT tore
   * the effect down and set it up again, and setting it up again moves focus
   * to the close button.
   *
   * What that looks like in use: you type one character into a field in the
   * panel and the caret vanishes. It is worst for exactly the people the focus
   * trap was added for, and it affected the Ask TESSMA AI drawer, every modal
   * and every side sheet in the product.
   *
   * The handler is read through a ref, so the effect always calls the current
   * one while depending only on `open`. No frozen value, size, colour or
   * layout changes; a dialog that is open simply stays where the person put
   * their attention.
   */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    restoreTo.current = document.activeElement as HTMLElement | null;

    const container = ref.current;
    const focusable = (): HTMLElement[] =>
      Array.from(
        container?.querySelectorAll<HTMLElement>(
          'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );

    focusable()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = focusable();
      const first = items[0];
      const last = items[items.length - 1];
      if (first === undefined || last === undefined) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      restoreTo.current?.focus();
    };
  }, [open]);

  return ref;
}

/* ------------------------------------------------------------------ */
/* Modal                                                               */
/* ------------------------------------------------------------------ */

export interface ModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly subtitle?: ReactNode;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly footer?: ReactNode;
  readonly footNote?: ReactNode;
  readonly children: ReactNode;
}

const MODAL_SIZE = { sm: styles.modalSm, md: styles.modalMd, lg: styles.modalLg } as const;

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  footer,
  footNote,
  children,
}: ModalProps) {
  const ref = useDialogBehaviour(open, onClose);
  const titleId = useId();
  if (!open) return null;

  return (
    <div
      className={`${styles.scrim} ${styles.scrimCentre}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`${styles.modal} ${MODAL_SIZE[size]}`}
      >
        <header className={styles.head}>
          <div className={styles.headText}>
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
            {subtitle !== undefined && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          <span className={styles.headClose}>
            <IconButton label="Close dialog" size="sm" bare onClick={onClose}>
              <Icon name="x" size={14} />
            </IconButton>
          </span>
        </header>

        <div className={styles.body}>{children}</div>

        {(footer !== undefined || footNote !== undefined) && (
          <footer className={styles.foot}>
            {footNote !== undefined && <span className={styles.footNote}>{footNote}</span>}
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Confirmation dialog                                                 */
/* ------------------------------------------------------------------ */

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
  readonly title: string;
  readonly tone?: 'danger' | 'warning' | 'info';
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly busy?: boolean;
  readonly children: ReactNode;
}

const CONFIRM_TONE = {
  danger: {
    icon: styles.confirmDanger,
    glyph: 'warning-circle' as IconName,
    button: 'destructive' as ButtonVariant,
  },
  warning: {
    icon: styles.confirmWarning,
    glyph: 'warning' as IconName,
    button: 'primary' as ButtonVariant,
  },
  info: { icon: styles.confirmInfo, glyph: 'info' as IconName, button: 'primary' as ButtonVariant },
};

export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  tone = 'danger',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  busy = false,
  children,
}: ConfirmDialogProps) {
  const config = CONFIRM_TONE[tone];
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={config.button} onClick={onConfirm} disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span className={`${styles.confirmIcon} ${config.icon}`} aria-hidden="true">
          <Icon name={config.glyph} size={16} />
        </span>
        <div>{children}</div>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Side sheet / edit drawer                                            */
/* ------------------------------------------------------------------ */

export interface SideSheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly subtitle?: ReactNode;
  readonly width?: 'narrow' | 'wide';
  readonly footer?: ReactNode;
  readonly children: ReactNode;
}

export function SideSheet({
  open,
  onClose,
  title,
  subtitle,
  width = 'narrow',
  footer,
  children,
}: SideSheetProps) {
  const ref = useDialogBehaviour(open, onClose);
  const titleId = useId();
  if (!open) return null;

  return (
    <div
      className={`${styles.scrim} ${styles.scrimRight}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`${styles.sheet} ${width === 'wide' ? styles.sheetWide : styles.sheetNarrow}`}
      >
        <header className={styles.head}>
          <div className={styles.headText}>
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
            {subtitle !== undefined && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          <span className={styles.headClose}>
            <IconButton label="Close panel" size="sm" bare onClick={onClose}>
              <Icon name="x" size={14} />
            </IconButton>
          </span>
        </header>
        <div className={styles.body}>{children}</div>
        {footer !== undefined && <footer className={styles.foot}>{footer}</footer>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tooltip, popover, menu                                              */
/* ------------------------------------------------------------------ */

export function Tooltip({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  const [visible, setVisible] = useState(false);
  const id = useId();

  return (
    <span
      className={styles.anchor}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      <span aria-describedby={visible ? id : undefined}>{children}</span>
      {visible && (
        <span className={styles.tooltip} role="tooltip" id={id}>
          {label}
        </span>
      )}
    </span>
  );
}

export interface PopoverProps {
  readonly trigger: (props: {
    readonly onClick: () => void;
    readonly 'aria-expanded': boolean;
  }) => ReactNode;
  readonly align?: 'left' | 'right';
  readonly label: string;
  readonly children: ReactNode;
}

export function Popover({ trigger, align = 'left', label, children }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  return (
    <span
      className={styles.anchor}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) close();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') close();
      }}
    >
      {trigger({ onClick: () => setOpen((current) => !current), 'aria-expanded': open })}
      {open && (
        <div
          className={[styles.popover, align === 'right' ? styles.popoverRight : '']
            .filter(Boolean)
            .join(' ')}
          role="dialog"
          aria-label={label}
        >
          {children}
        </div>
      )}
    </span>
  );
}

export interface MenuItem {
  readonly id: string;
  readonly label: string;
  /** A name from the frozen §16 icon vocabulary. */
  readonly icon?: IconName;
  readonly danger?: boolean;
  readonly disabled?: boolean;
  readonly separatorBefore?: boolean;
  readonly onSelect: () => void;
}

export interface ActionMenuProps {
  readonly label: string;
  readonly items: ReadonlyArray<MenuItem>;
  readonly trigger?: ReactNode;
}

/** Action / context menu with the standard roving-focus keyboard contract. */
export function ActionMenu({ label, items, trigger }: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const enabled = items.filter((item) => item.disabled !== true);

  const onKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>): void => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((index) => (index + delta + enabled.length) % enabled.length);
    }
    if (event.key === 'Enter') {
      const item = enabled[activeIndex];
      if (item !== undefined) {
        event.preventDefault();
        item.onSelect();
        setOpen(false);
      }
    }
  };

  return (
    <span
      className={styles.anchor}
      onKeyDown={onKeyDown}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <span onClick={() => setOpen((current) => !current)}>
        {trigger ?? (
          <IconButton label={label} size="sm" aria-haspopup="menu" aria-expanded={open}>
            <Icon name="dots-three" size={16} />
          </IconButton>
        )}
      </span>
      {open && (
        <ul className={styles.menu} role="menu" aria-label={label}>
          {items.map((item) => (
            <li key={item.id} role="none">
              {item.separatorBefore === true && (
                <span className={styles.menuSeparator} role="separator" />
              )}
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                className={[styles.menuItem, item.danger === true ? styles.menuItemDanger : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => {
                  item.onSelect();
                  setOpen(false);
                }}
              >
                {item.icon !== undefined && (
                  <span className={styles.menuIcon} aria-hidden="true">
                    <Icon name={item.icon} size={14} />
                  </span>
                )}
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}
